import { Action, createReducer, on } from '@ngrx/store';
import { IIdentityManagementState } from '../../utils/interfaces/store.interfaces';
import { clearPermissionDataAction } from '../actions/app.actions';
import {
  getAuthInfoIdentityManagementFailedAction,
  getAuthInfoIdentityManagementSuccessAction,
  setCompanyAction,
  getUsersSuccessAction,
  getUsersFailedAction,
  getDealerCompaniesSuccessAction,
  getDealerCompaniesFailedAction,
  getInstallerCompaniesFailedAction,
  getInstallerCompaniesSuccessAction,
  getRetailerCompaniesFailedAction,
  getRetailerCompaniesSuccessAction,
  getUserFailedAction,
  getUserSuccessAction,
  getCompaniesSuccessAction,
  getCompanyInfoSuccessAction,
  updateCompanySuccessAction,
  createCompanySuccessAction,
  getAuthInfoForCompanySuccessAction,
  getAuthInfoForUserSuccessAction,
  getUserDetailsSuccessAction,
  getUserDetailsFailedAction,
  updateUserDetailsSuccessAction,
  createUserSuccessAction,
  deleteUserDetailsSuccessAction,
  getLinkedInstallersSuccessAction,
  getLinkedInstallersFailedAction,
  unlinkInstallerSuccessAction,
  linkInstallerSuccessAction,
  createCcapTokensSuccessAction,
  getCcapTokensSuccessAction,
  revokeCcapTokenSuccessAction
} from '../actions/identitymanagement.actions'
import { Company, CompanyDetails } from 'src/app/generated/CrossPoint.IdentityManagement.Server';

const initialState: IIdentityManagementState = {
  selectedCompany: null,
  permission: null,
  users: null,
  user: null,
  userDetails: null,
  linkedCompanies: null,
  dealerCompanies: null,
  retailerCompanies: null,
  installerCompanies: null,
  allCompaniesSearch: null,
  ccapTokens: null,
  companyPermissions: null,
  userPermissions: null,
  companyCache: new Map(),
  companyInfoCache: new Map(),
};

function updateCompanyCache(cache: ReadonlyMap<string, Company>, companies: Company[]): Map<string, Company> {
  const newMap = new Map(cache);
  for (const company of companies) {
    newMap.set(company.companyId, company);
  }
  return newMap;
}

function updateCompanyInfoCache(cache: ReadonlyMap<string, CompanyDetails>, companies: CompanyDetails[]): Map<string, CompanyDetails> {
  const newMap = new Map(cache);
  for (const company of companies) {
    newMap.set(company.companyId, company);
  }
  return newMap;
}

export const identityManagementReducerImplicit = createReducer(
  initialState,
  on(getAuthInfoIdentityManagementSuccessAction, (state, { data: permission }) => ({
    ...state,
    permission,
  })),
  on(getAuthInfoIdentityManagementFailedAction, (state,) => ({
    ...state,
    permission: null,
  })),
  on(getAuthInfoForCompanySuccessAction, (state, { data }) => ({
    ...state,
    companyPermissions: data
  })),
  on(getAuthInfoForUserSuccessAction, (state, { data }) => ({
    ...state,
    userPermissions: data
  })),
  on(clearPermissionDataAction, (state) => ({
    ...state,
    permission: null,
  })),
  on(getDealerCompaniesSuccessAction, (state, { data: dealerCompanies }) => ({
    ...state,
    dealerCompanies,
    companyCache: updateCompanyCache(state.companyCache, dealerCompanies),
  })),
  on(getDealerCompaniesFailedAction, (state,) => ({
    ...state,
    dealerCompanies: null,
  })),
  on(getRetailerCompaniesSuccessAction, (state, { data: retailerCompanies }) => ({
    ...state,
    retailerCompanies,
    companyCache: updateCompanyCache(state.companyCache, retailerCompanies),
  })),
  on(getRetailerCompaniesFailedAction, (state,) => ({
    ...state,
    retailerCompanies: null,
  })),
  on(getInstallerCompaniesSuccessAction, (state, { data: installerCompanies }) => ({
    ...state,
    installerCompanies,
    companyCache: updateCompanyCache(state.companyCache, installerCompanies),
  })),
  on(getInstallerCompaniesFailedAction, (state,) => ({
    ...state,
    retailerCompanies: null,
  })),
  on(getCompaniesSuccessAction, (state, { data: companies }) => ({
    ...state,
    allCompaniesSearch: companies,
    companyCache: updateCompanyCache(state.companyCache, companies),
  })),
  on(getCompanyInfoSuccessAction, (state, { data: companyInfo }) => ({
    ...state,
    companyCache: updateCompanyCache(state.companyCache, [companyInfo]),
    companyInfoCache: updateCompanyInfoCache(state.companyInfoCache, [companyInfo]),
  })),
  on(updateCompanySuccessAction, (state, { data: companyInfo }) => ({
    ...state,
    companyCache: updateCompanyCache(state.companyCache, [companyInfo]),
    companyInfoCache: updateCompanyInfoCache(state.companyInfoCache, [companyInfo]),
  })),
  on(createCompanySuccessAction, (state, { data: companyInfo }) => ({
    ...state,
    companyCache: updateCompanyCache(state.companyCache, [companyInfo]),
    companyInfoCache: updateCompanyInfoCache(state.companyInfoCache, [companyInfo]),
  })),
  on(setCompanyAction, (state, { data: selectedCompany }) => ({
    ...state,
    selectedCompany,
  })),

  on(getUsersSuccessAction, (state, { data: users }) => ({
    ...state,
    users,
  })),
  on(getUsersFailedAction, (state,) => ({
    ...state,
    users: null,
  })),
  on(getUserSuccessAction, (state, { data: user }) => ({
    ...state,
    user,
  })),
  on(getUserFailedAction, (state,) => ({
    ...state,
    user: null,
  })),
  on(getUserDetailsSuccessAction, (state, { data: userDetails }) => ({
    ...state,
    userDetails,
  })),
  on(getUserDetailsFailedAction, (state,) => ({
    ...state,
    userDetails: null,
  })),
  on(updateUserDetailsSuccessAction, (state, { data: userDetails }) => ({
    ...state,
    userDetails,
  })),
  on(createUserSuccessAction, (state, { data: userDetails }) => ({
    ...state,
    userDetails,
  })),
  on(deleteUserDetailsSuccessAction, (state,) => ({
    ...state,
    userDetails: null,
  })),
  on(getLinkedInstallersSuccessAction, (state, { data: linkedCompanies }) => ({
    ...state,
    linkedCompanies,
  })),
  on(getLinkedInstallersFailedAction, (state,) => ({
    ...state,
    linkedCompanies: null
  })),
  on(unlinkInstallerSuccessAction, (state, { data: linkedCompanies }) => ({
    ...state,
    linkedCompanies,
  })),
  on(linkInstallerSuccessAction, (state, { data: linkedCompanies }) => ({
    ...state,
    linkedCompanies,
  })),
  on(createCcapTokensSuccessAction, (state, { data: ccapTokens }) => ({
    ...state,
    ccapTokens: [...(state.ccapTokens ?? []), ...ccapTokens],
  })),
  on(getCcapTokensSuccessAction, (state, { data: ccapTokens }) => ({
    ...state,
    ccapTokens: ccapTokens,
  })),
  on(revokeCcapTokenSuccessAction, (state, { data: ccapToken }) => ({
    ...state,
    ccapTokens: [...state.ccapTokens.filter(t => t.id !== ccapToken.id), ccapToken],
  })),
);

export function identityManagementReducer(state: any, action: Action): IIdentityManagementState {
  return identityManagementReducerImplicit(state, action);
}
